/**
 * 屏 15 — 通用过渡动画 v2
 *
 * 参考设计语言（FORGE → QUENCH 调性）：
 * 深海军蓝底 + 极淡45°对角线 + 45° 旋转铭文 + 青色对角装饰线 + 人像剪影
 * Phase 1: 对角线描画 + 节点浮现 + 代码文字 (0-600ms)
 * Phase 2: 铭文浮现 (200-900ms, 与 Phase 1 重叠)
 * Phase 3: 淡出跳转 (900-1200ms)
 * 任意时刻点击/按键可跳过
 */

// ============================================================
// 过渡目标传递：双重保险（模块级状态 + sessionStorage）
// ============================================================

const TRANSITION_STORAGE_KEY = "battlecode-transition-target";

export interface TransitionTarget {
  /** serif 大字标题，如 "铸剑于阁" */
  title: string;
  /** mono 协议编号，如 "FORGE.PROTOCOL // 0x01" */
  protocol: string;
  /** CSS var 主题色，如 "var(--tactical-green)" */
  accentColor: string;
  /** 目标路由 hash，如 "#/forge" */
  targetHash: string;
  /** mono 描述文字，如 "锻代码为剑，淬战术为锋" */
  subtitle?: string;
}

let _pendingTarget: TransitionTarget | null = null;

export function setTransitionTarget(target: TransitionTarget): void {
  _pendingTarget = target;
  try {
    sessionStorage.setItem(TRANSITION_STORAGE_KEY, JSON.stringify(target));
  } catch (_) {
    /* sessionStorage 不可用时静默降级 */
  }
}

export function getTransitionTarget(): TransitionTarget | null {
  return _pendingTarget;
}

// ============================================================
// 主渲染函数
// ============================================================

export function renderScreen15Transition(
  app: HTMLElement,
  onNavigate: (path: string) => void
): void {
  // 双层读取：优先模块状态，降级 sessionStorage
  let raw = _pendingTarget;
  if (!raw) {
    try {
      const stored = sessionStorage.getItem(TRANSITION_STORAGE_KEY);
      if (stored) raw = JSON.parse(stored) as TransitionTarget;
    } catch (_) { /* ignore */ }
  }
  try { sessionStorage.removeItem(TRANSITION_STORAGE_KEY); } catch (_) { /* ignore */ }

  if (!raw) {
    app.textContent = "TRANSITION_ERROR: NO_TARGET";
    onNavigate("#/armory-hall");
    return;
  }

  const target = raw;
  _pendingTarget = null;

  // 提取 protocol 前缀（"FORGE" from "FORGE.PROTOCOL // 0x01"）
  const protoPrefix = target.protocol.split(".")[0];
  // 对角线铭文：PROTO → 标题
  const diagLabel = `${protoPrefix} → ${target.title}`;

  // ---- DOM 结构 ----
  app.innerHTML = `
    <div class="s15-transition-page" id="s15-transition-page">
      <canvas id="s15-bg-canvas"></canvas>
      <div class="s15-content" id="s15-content">
        <div class="s15-diag-label" id="s15-diag-label">${diagLabel}</div>
        <div class="s15-protocol" id="s15-protocol" style="visibility: hidden;"></div>
        <div class="s15-subtitle" id="s15-subtitle" style="visibility: hidden;"></div>
      </div>
      <div class="s15-hud-corners" id="s15-hud-corners" style="opacity: 0;">
        <div class="hud-corner tl"></div>
        <div class="hud-corner tr"></div>
        <div class="hud-corner bl"></div>
        <div class="hud-corner br"></div>
      </div>
      <div class="s15-status-bar">TRANSITION // ${protoPrefix}</div>
    </div>
  `;

  // ---- 元素引用 ----
  const transitionPage = app.querySelector("#s15-transition-page") as HTMLElement;
  const diagLabelEl = app.querySelector("#s15-diag-label") as HTMLElement;
  const protoEl = app.querySelector("#s15-protocol") as HTMLElement;
  const subtitleEl = app.querySelector("#s15-subtitle") as HTMLElement;
  const cornersEl = app.querySelector("#s15-hud-corners") as HTMLElement;
  const canvas = app.querySelector("#s15-bg-canvas") as HTMLCanvasElement;

  // ---- 生命周期 ----
  let skipped = false;
  let disposed = false;
  let rafId = 0;

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    cancelAnimationFrame(rafId);
    app.removeEventListener("click", skip);
    window.removeEventListener("keydown", keyHandler);
    window.removeEventListener("resize", resizeCanvas);
    transitionPage.style.removeProperty("opacity");
  }

  function skip(): void {
    if (skipped || disposed) return;
    skipped = true;
    dispose();
    onNavigate(target.targetHash);
  }

  app.addEventListener("click", skip, { once: true });
  const keyHandler = (e: KeyboardEvent) => {
    if (e.key === "Escape" || e.key === " " || e.key === "Enter") skip();
  };
  window.addEventListener("keydown", keyHandler, { once: true });

  // ---- Canvas 背景：深海军蓝 + 极淡45°对角线 + 人像剪影 + 青色装饰线 + 代码文字 ----
  function drawBackground(ctx: CanvasRenderingContext2D, w: number, h: number) {
    // A. 底色 — 深海军蓝
    ctx.fillStyle = "#0A1929";
    ctx.fillRect(0, 0, w, h);

    // B. 45° 极淡对角线网格（白 1.5% 不透明度，64px 间距）
    ctx.save();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.015)";
    ctx.lineWidth = 0.5;
    const spacing = 64;
    const diag = Math.ceil((w + h) / spacing);
    for (let i = -diag; i < diag; i++) {
      const offset = i * spacing;
      ctx.beginPath();
      ctx.moveTo(offset, 0);
      ctx.lineTo(offset - h, h);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(offset, 0);
      ctx.lineTo(offset + h, h);
      ctx.stroke();
    }
    ctx.restore();

    // C. 青色对角装饰线（从左上 12% 到右下 88%，避免触及角标区域）
    const x1 = w * 0.12;
    const y1 = h * 0.12;
    const x2 = w * 0.88;
    const y2 = h * 0.88;

    ctx.save();
    ctx.strokeStyle = "rgba(0, 228, 255, 0.2)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    // 对角线上等距分布的 7 个小圆点节点
    const NODES = 7;
    for (let i = 0; i < NODES; i++) {
      const t = (i + 1) / (NODES + 1); // 0.125 ~ 0.875
      const nx = x1 + (x2 - x1) * t;
      const ny = y1 + (y2 - y1) * t;

      // 外圈
      ctx.beginPath();
      ctx.arc(nx, ny, 2.5, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(0, 228, 255, 0.3)";
      ctx.lineWidth = 0.8;
      ctx.stroke();

      // 内点（发光感）
      ctx.beginPath();
      ctx.arc(nx, ny, 1, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(0, 228, 255, 0.5)";
      ctx.fill();
    }
    ctx.restore();

    // D. 右侧人像剪影（淡出抽象轮廓 + 虚线框）
    ctx.save();
    const silhouetteCX = w * 0.72;
    const silhouetteCY = h * 0.48;
    const silhouetteW = w * 0.18;
    const silhouetteH = h * 0.35;

    // 半透明虚线框
    ctx.setLineDash([4, 8]);
    ctx.strokeStyle = "rgba(0, 228, 255, 0.06)";
    ctx.lineWidth = 1;
    ctx.strokeRect(
      silhouetteCX - silhouetteW / 2,
      silhouetteCY - silhouetteH / 2,
      silhouetteW,
      silhouetteH
    );
    ctx.setLineDash([]);

    // 抽象人形轮廓（CSS 位置：稍微偏右的淡出轮廓）
    const sx = silhouetteCX;
    const sy = silhouetteCY;
    const scale = Math.min(silhouetteW, silhouetteH) * 0.4;

    ctx.fillStyle = "rgba(255, 255, 255, 0.03)";
    ctx.beginPath();
    // 头部
    ctx.arc(sx, sy - scale * 0.9, scale * 0.15, 0, Math.PI * 2);
    ctx.fill();
    // 身体（梯形）
    ctx.beginPath();
    ctx.moveTo(sx - scale * 0.25, sy - scale * 0.65);
    ctx.lineTo(sx + scale * 0.25, sy - scale * 0.65);
    ctx.lineTo(sx + scale * 0.35, sy + scale * 0.3);
    ctx.lineTo(sx - scale * 0.35, sy + scale * 0.3);
    ctx.closePath();
    ctx.fill();
    // 双臂（两段线）
    ctx.save();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.025)";
    ctx.lineWidth = scale * 0.08;
    ctx.beginPath();
    ctx.moveTo(sx - scale * 0.22, sy - scale * 0.55);
    ctx.lineTo(sx - scale * 0.55, sy - scale * 0.1);
    ctx.moveTo(sx + scale * 0.22, sy - scale * 0.55);
    ctx.lineTo(sx + scale * 0.55, sy - scale * 0.1);
    ctx.stroke();
    ctx.restore();

    ctx.restore();

    // E. 右下代码文字叠加
    ctx.save();
    ctx.font = "10px monospace";
    ctx.fillStyle = "rgba(139, 149, 165, 0.06)";

    const codeLines = [
      `int ${protoPrefix.toLowerCase()}_phase = 0x01;`,
      `position = (${Math.round(x2)}, ${Math.round(y2)});`,
      `arm(target); calibrate();`,
      `// ${target.title}`,
      `${target.protocol.split(" // ")[0]}`,
    ];

    const codeStartX = w * 0.52;
    const codeStartY = h * 0.7;
    const codeLineHeight = 14;
    codeLines.forEach((line, i) => {
      ctx.fillText(line, codeStartX, codeStartY + i * codeLineHeight);
    });
    ctx.restore();
  }

  function resizeCanvas() {
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.clientWidth * dpr;
    canvas.height = canvas.clientHeight * dpr;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawBackground(ctx, canvas.clientWidth, canvas.clientHeight);
    }
  }

  resizeCanvas();
  window.addEventListener("resize", resizeCanvas);

  // ---- 文本准备 ----
  // 打字机效果 protocol
  function buildProtoHTML(progress: number): string {
    const full = target.protocol;
    const visible = Math.floor(progress * full.length);
    return full.slice(0, visible) + (visible < full.length ? "_" : "");
  }

  // ---- 动画循环 ----
  const startTime = performance.now();
  const DURATION = 1200;

  function animate(now: number) {
    if (skipped) return;
    const elapsed = now - startTime;
    const t = Math.min(elapsed / DURATION, 1);

    // Phase 1: 对角线标签淡入（0-600ms）
    const diagProgress = Math.max(0, Math.min(1, elapsed / 600));
    if (diagLabelEl) {
      diagLabelEl.style.opacity = String(Math.min(0.5, diagProgress * 0.5));
    }

    // Phase 2: 铭文浮现（200-900ms，与对角线尾部重叠）
    const textProgress = Math.max(0, Math.min(1, (elapsed - 200) / 700));
    if (protoEl) {
      protoEl.innerHTML = buildProtoHTML(textProgress);
      protoEl.style.visibility = textProgress > 0.1 ? "visible" : "hidden";
    }
    if (subtitleEl && target.subtitle) {
      subtitleEl.textContent = target.subtitle;
      subtitleEl.style.visibility = textProgress > 0.3 ? "visible" : "hidden";
      subtitleEl.style.opacity = String(Math.min(1, (textProgress - 0.3) / 0.5));
    }

    // HUD 括号淡入（max opacity 0.15）
    if (cornersEl) {
      cornersEl.style.opacity = String(Math.min(0.15, textProgress * 0.15));
    }

    // Phase 3: 淡出（t >= 0.75 即 ~900ms）
    if (t > 0.75) {
      const fadeProgress = (t - 0.75) / 0.25;
      transitionPage.style.opacity = String(1 - fadeProgress);
    }

    if (t >= 1) {
      skip();
      return;
    }

    rafId = requestAnimationFrame(animate);
  }

  rafId = requestAnimationFrame(animate);

  // ---- Cleanup ----
  (app as any)._s15Cleanup = dispose;
}
